class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.sectionId = this.dataset.sectionId || 'predictive-search';
    this.minimumCharacters = Number(this.dataset.minimumCharacters) || 1;
    this.resultsLimit = Number(this.dataset.resultsLimit) || 10;

    this.setupEventListeners();
    this.predictiveSearchResults.addEventListener('click', this.onResultsClick.bind(this));
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (this.searchTerm.length < this.minimumCharacters) {
      this.closeResults();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (currentSearchTerm.length < this.minimumCharacters) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      'a[aria-selected="true"], [aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm, page = 1, forceFallback = false) {
    const queryKey = `${this.sectionId}:${this.resultsLimit}:${page}:${searchTerm.replace(' ', '-').toLowerCase()}`;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: this.sectionId,
      'resources[limit]': String(this.resultsLimit),
    });
    if (this.dataset.resourceType) params.set('resources[type]', this.dataset.resourceType);

    const shopifyFeatures = document.querySelector('#shopify-features');
    let predictiveSearchSupported = true;

    try {
      predictiveSearchSupported = JSON.parse(shopifyFeatures?.textContent || '{}').predictiveSearch !== false;
    } catch (error) {
      // Keep Shopify's predictive endpoint as the default if the feature payload is unavailable.
    }

    const fallbackParams = new URLSearchParams({
      q: searchTerm,
      section_id: 'yuan-predictive-search',
      type: 'product',
      'options[prefix]': 'last',
      page: String(page),
    });
    predictiveSearchSupported = predictiveSearchSupported && !forceFallback && page === 1;
    const requestUrl = predictiveSearchSupported
      ? `${routes.predictive_search_url}?${params}`
      : `${routes.search_url || '/search'}?${fallbackParams}`;
    let responseSectionId = predictiveSearchSupported ? this.sectionId : 'yuan-predictive-search';

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => {
        if (response.status === 417) {
          responseSectionId = 'yuan-predictive-search';
          return fetch(`${routes.search_url || '/search'}?${fallbackParams}`, {
            signal: this.abortController.signal,
          });
        }

        return response;
      })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${responseSectionId}`);
        if (!section) throw new Error(`Predictive search section not found: ${responseSectionId}`);
        const resultsMarkup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          if ((instance.dataset.sectionId || 'predictive-search') === this.sectionId) {
            instance.cachedResults[queryKey] = resultsMarkup;
          }
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  onResultsClick(event) {
    const addButton = event.target.closest('[data-yuan-add-to-cart]');
    if (addButton) {
      this.onQuickAddClick(addButton);
      return;
    }

    const quantityButton = event.target.closest('[data-yuan-quantity-change]');
    if (quantityButton) {
      const quantity = quantityButton.parentElement.querySelector('input');
      const nextValue = Math.max(Number(quantity.min) || 1, Number(quantity.value || 1) + Number(quantityButton.dataset.yuanQuantityChange));
      quantity.value = nextValue;
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const pageButton = event.target.closest('[data-yuan-search-page]');
    if (!pageButton) return;

    event.preventDefault();
    this.getSearchResults(this.searchTerm, Number(pageButton.dataset.yuanSearchPage), true);
    this.predictiveSearchResults.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onQuickAddClick(button) {
    const result = button.closest('.yuan-live-search__result');
    const variantId = result.querySelector('.yuan-live-search__variant-select')?.value || button.dataset.variantId;
    const quantity = Math.max(1, Number(result.querySelector('.yuan-live-search__quantity input')?.value) || 1);
    const buttonText = button.querySelector('span');
    const originalText = buttonText?.textContent;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData();

    formData.append('id', variantId);
    formData.append('quantity', String(quantity));
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');

    if (cart) {
      formData.append(
        'sections',
        cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',')
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(button);
    }

    fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || data.status) throw new Error(data.description || data.message || 'Unable to add item');
        this.closest('details-modal')?.close();
        if (cart) {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
        }
        if (buttonText) buttonText.textContent = 'تمت الإضافة';
        setTimeout(() => {
          if (buttonText) buttonText.textContent = originalText;
        }, 1400);
      })
      .catch((error) => {
        console.error(error);
        if (buttonText) buttonText.textContent = error.message;
      })
      .finally(() => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    return `${Math.max(240, Math.floor(window.innerHeight - headerBottom - 16))}px`;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.getResultsMaxHeight();
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
